import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, CheckCircle2, XCircle, KeyRound, Lock } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import PasswordInput from "@/components/PasswordInput";
import { adminApi } from "@/lib/api";
import { roleLabel } from "@/lib/permissions";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState({ username: "", password: "", role: "responsable" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.users();
      setUsers(data);
    } catch (err) {
      toast.error("Impossible de charger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formState.username || !formState.password) return;
    setSaving(true);
    try {
      await adminApi.createUser(formState);
      toast.success("Utilisateur créé");
      setFormState({ username: "", password: "", role: "responsable" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (u) => {
    try {
      await adminApi.validateUser(u.id);
      toast.success(u.validated ? "Compte invalidé" : "Compte validé");
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer l'utilisateur "${u.username}" ?`)) return;
    try {
      await adminApi.deleteUser(u.id);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handlePasswordReset = async (u) => {
    const newPwd = window.prompt(`Nouveau mot de passe pour "${u.username}" :`);
    if (!newPwd) return;
    try {
      await adminApi.updateUser(u.id, { password: newPwd });
      toast.success("Mot de passe mis à jour");
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleRoleToggle = async (u) => {
    const cycle = ["responsable", "expert", "admin"];
    const current = u.role === "user" ? "responsable" : u.role || "responsable";
    const idx = cycle.indexOf(current);
    const newRole = cycle[(idx + 1) % cycle.length];
    if (!window.confirm(`Changer le rôle de "${u.username}" en "${newRole}" ?`)) return;
    try {
      await adminApi.updateUser(u.id, { role: newRole });
      toast.success(`Rôle changé : ${newRole}`);
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader
        title="Utilisateurs"
        subtitle={`${users.length} compte(s)`}
        right={
          <button
            data-testid="user-create-toggle-btn"
            onClick={() => setShowForm((s) => !s)}
            className="px-3 py-2 rounded-xl bg-[var(--vidoc-primary)] text-white text-sm font-medium flex items-center gap-1.5 touch-target"
          >
            <Plus className="w-4 h-4" />
            Nouveau
          </button>
        }
      />

      <div className="px-4 md:px-8 py-6 max-w-3xl space-y-4">
        {showForm && (
          <form
            onSubmit={handleCreate}
            className="bg-white rounded-xl border border-[var(--vidoc-border)] p-4 space-y-3 animate-slideUp"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                data-testid="new-user-username"
                placeholder="Identifiant"
                value={formState.username}
                onChange={(e) => setFormState({ ...formState, username: e.target.value })}
                className="px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] outline-none focus:ring-2 ring-[var(--vidoc-primary)]"
              />
              <PasswordInput
                data-testid="new-user-password"
                placeholder="Mot de passe"
                value={formState.password}
                onChange={(e) => setFormState({ ...formState, password: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] outline-none focus:ring-2 ring-[var(--vidoc-primary)]"
              />
              <select
                data-testid="new-user-role"
                value={formState.role}
                onChange={(e) => setFormState({ ...formState, role: e.target.value })}
                className="px-3 py-2.5 rounded-lg bg-[var(--vidoc-bg-secondary)] outline-none focus:ring-2 ring-[var(--vidoc-primary)]"
              >
                <option value="responsable">Responsable</option>
                <option value="expert">Expert</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>
            <button
              data-testid="new-user-submit"
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-[var(--vidoc-primary)] text-white font-medium touch-target disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Créer
            </button>
          </form>
        )}

        {loading ? (
          <div className="text-center py-6 text-[var(--vidoc-text-secondary)]">
            Chargement...
          </div>
        ) : (
          <>
            <div
              data-testid="admin-users-encrypted-note"
              className="-mt-1 px-3 py-2 rounded-lg bg-blue-50 text-xs text-[var(--vidoc-primary)] flex items-center gap-2"
            >
              <Lock className="w-3.5 h-3.5 shrink-0" />
              <span>
                Les mots de passe sont chiffrés (bcrypt) et ne peuvent plus
                être affichés. Cliquez sur l'icône <KeyRound className="inline w-3 h-3" /> pour en réinitialiser un.
              </span>
            </div>
            <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
            {users.map((u) => {
              const isAdmin = u.role === "admin";
              const isExpert = u.role === "expert";
              const isResp = u.role === "responsable" || u.role === "user";
              return (
              <div
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {u.username}
                    {isAdmin && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[var(--vidoc-primary)]">
                        ADMIN
                      </span>
                    )}
                    {isResp && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-[var(--vidoc-success)]">
                        RESPONSABLE
                      </span>
                    )}
                    {isExpert && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
                        EXPERT
                      </span>
                    )}
                    {!u.validated && !isAdmin && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-50 text-[var(--vidoc-warning)]">
                        EN ATTENTE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--vidoc-text-secondary)] flex items-center gap-2 mt-0.5">
                    <span className="font-medium">Mot de passe :</span>
                    <code className="px-2 py-0.5 rounded bg-[var(--vidoc-bg-secondary)] text-[var(--vidoc-text)] font-mono">
                      ••••••••
                    </code>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  {u.role !== "admin" && (
                    <button
                      data-testid={`user-validate-${u.id}`}
                      onClick={() => handleValidate(u)}
                      className="p-2 rounded-lg hover:bg-[var(--vidoc-bg-secondary)] touch-target"
                      title={u.validated ? "Invalider" : "Valider"}
                    >
                      {u.validated ? (
                        <CheckCircle2 className="w-5 h-5 text-[var(--vidoc-success)]" />
                      ) : (
                        <XCircle className="w-5 h-5 text-[var(--vidoc-warning)]" />
                      )}
                    </button>
                  )}
                  <button
                    data-testid={`user-password-${u.id}`}
                    onClick={() => handlePasswordReset(u)}
                    className="p-2 rounded-lg hover:bg-[var(--vidoc-bg-secondary)] touch-target"
                    title="Changer le mot de passe"
                  >
                    <KeyRound className="w-5 h-5 text-[var(--vidoc-text-secondary)]" />
                  </button>
                  <button
                    data-testid={`user-role-${u.id}`}
                    onClick={() => handleRoleToggle(u)}
                    className="px-2 py-1 text-xs rounded-lg hover:bg-[var(--vidoc-bg-secondary)] text-[var(--vidoc-text-secondary)] border border-[var(--vidoc-border)]"
                    title="Changer le rôle"
                  >
                    Rôle : {roleLabel(u.role)}
                  </button>
                  <button
                    data-testid={`user-delete-${u.id}`}
                    onClick={() => handleDelete(u)}
                    className="p-2 rounded-lg hover:bg-red-50 text-[var(--vidoc-danger)] touch-target"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              );
            })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
